import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    OnInit,
    Signal,
    viewChild,
} from "@angular/core";
import { UserStore } from "../../../stores/users/user.store";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { Nullable } from "@playcharge/common/models/backend/nullable";
import { UserBackofficeViewModel } from "@playcharge/common/models/backend/users/backoffice/user-backoffice-view-model";
import { UserConversionBackofficeViewModel } from "@playcharge/common/models/backend/users/backoffice/user-conversion-backoffice-view-model";
import { UserTransactionBackofficeViewModel } from "@playcharge/common/models/backend/users/backoffice/user-transaction-backoffice-view-model";
import { MatPaginator } from "@angular/material/paginator";
import { MatCheckbox } from "@angular/material/checkbox";
import { ToastrService } from "ngx-toastr";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatButton } from "@angular/material/button";
import { CdkTextareaAutosize } from "@angular/cdk/text-field";
import { MatTabGroup, MatTab } from "@angular/material/tabs";
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { ComponentRoutes } from "../../../models/constants/component-routes";
import { NoScrollDirective } from "../../../directives/noScrollDirective";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { ConfirmDialogComponent } from "../../dialogs/confirm-dialog/confirm-dialog.component";
import { MatDialog } from "@angular/material/dialog";

@Component({
    selector: "app-user",
    standalone: true,
    imports: [CommonModule, MatTableModule, MatPaginator, MatCheckbox, MatFormField, MatLabel, MatInput, MatButton, CdkTextareaAutosize, MatTabGroup, MatTab, ReactiveFormsModule, NoScrollDirective, MatSortModule],
    providers: [UserStore],
    templateUrl: "./user.component.html",
    styleUrl: "./user.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserComponent implements OnInit {
    private _conversionsPaginator: Signal<Nullable<MatPaginator>> = viewChild('conversionsPaginator', { read: MatPaginator });
    private _transactionsPaginator: Signal<Nullable<MatPaginator>> = viewChild('transactionsPaginator', { read: MatPaginator });
    private _conversionsSort: Signal<Nullable<MatSort>> = viewChild("conversionsSort", { read: MatSort });
    private _transactionsSort: Signal<Nullable<MatSort>> = viewChild("transactionsSort", { read: MatSort }); 
    private _matDialog: MatDialog = inject(MatDialog);
    private _goalFilter: Signal<MatInput | undefined> = viewChild("goalFilter", { read: MatInput });
    private _rejectReasonFilter: Signal<MatInput | undefined> = viewChild("rejectReasonFilter", { read: MatInput });
    private _forceApproveReasonFilter: Signal<MatInput | undefined> = viewChild("forceApproveReasonFilter", { read: MatInput });

    public userStore: UserStore = inject(UserStore);
    private _toastrService = inject(ToastrService);
    private _router = inject(Router);

    public conversionsDataSource: MatTableDataSource<UserConversionBackofficeViewModel> =
        new MatTableDataSource<UserConversionBackofficeViewModel>();

    public transactionsDataSource: MatTableDataSource<UserTransactionBackofficeViewModel> =
        new MatTableDataSource<UserTransactionBackofficeViewModel>();

    constructor() {
        effect(() => this.paginatorChangeEffectFn());
        effect(() => this.sortChangeEffectFn());
        effect(() => this.userChangeEffectFn(), { 
            allowSignalWrites: true
        });
    }
    ngOnInit(): void {
        this.conversionsDataSource.filterPredicate = this.createFilter();
    }

    private userChangeEffectFn(): void {
        const user: Nullable<UserBackofficeViewModel> = this.userStore.user();

        if (user) {
            this.conversionsDataSource.data = user.conversions;
            this.transactionsDataSource.data = user.transactions;
        }
    }

    private paginatorChangeEffectFn() {
        const conversionsPaginator: Nullable<MatPaginator> = this._conversionsPaginator();
        const transactionsPaginator: Nullable<MatPaginator> = this._transactionsPaginator();

        if (conversionsPaginator) {
            this.conversionsDataSource.paginator = conversionsPaginator;
        }

        if (transactionsPaginator) {
            this.transactionsDataSource.paginator = transactionsPaginator;
        }
    }

    private sortChangeEffectFn() {
        const conversionsSort: Nullable<MatSort> = this._conversionsSort();
        const transactionsSort: Nullable<MatSort> = this._transactionsSort();

        if (conversionsSort) {
            this.conversionsDataSource.sort = conversionsSort;
        }

        if (transactionsSort) {
            this.transactionsDataSource.sort = transactionsSort;
        }
    }

    public reapproveConversion(conversionId: number): void {
        this._matDialog
            .open(ConfirmDialogComponent, {
                data: {
                    message: "Confirm your action to reapprove this conversion.",
                },
            })
            .afterClosed()
            .subscribe((result) => {
                if (result) {
                    this.userStore.reapproveConversion({
                        conversionId,
                        next: () => this._toastrService.success("Success"),
                        error: (error) => {
                            this._toastrService.error(error.error.description ?? "Internal Error");
                        },
                    });
                }
            });
    }
    public forceApproveConversion(conversionId: number, reason: string): void {
        this._matDialog
            .open(ConfirmDialogComponent, {
                data: {
                    message: "Confirm your action to force approve this conversion.",
                },
            })
            .afterClosed()
            .subscribe((result) => {
                if (result) {
                    this.userStore.forceApproveConversion({
                        conversionId,
                        reason: reason,
                        next: () => this._toastrService.success("Success"),
                        error: (error) => {
                            this._toastrService.error(error.error.description ?? "Internal Error");
                        },
                    });
                }
            });
    }

    public changeUserBalance(): void {
        const formValue = this.userBalanceForm.getRawValue();
        this.userStore.balanceCorrection({
            amount: formValue.amount,
            expAmount: formValue.expAmount,
            reason: formValue.reason,
            next: () => this._toastrService.success("Success"),
            error: (error) => {
                this._toastrService.error(error.error.description ?? "Internal Error");
            },
        });
    }
    
    public banUser(): void {
        this.userStore.banUser({
            userId: this.userStore.user()!.userId,
            reason: this.banUserForm.value.reason,
            next: (user) => {
                this._toastrService.success("Success")
                this._router.navigate(['/', ComponentRoutes.users.root, user.email]);
            },
            error: (error) => {
                this._toastrService.error(error.error.description ?? "Internal Error");
            },
        });
    }
    
    public unbanUser(): void {
        this.userStore.unbanUser({
            userId: this.userStore.user()!.userId,
            reason: this.banUserForm.value.reason,
            next: (user) => {
                this._toastrService.success("Success");
                this._router.navigate(['/', ComponentRoutes.users.root, user.email]);
            },
            error: (error) => {
                this._toastrService.error(error.error.description ?? "Internal Error");
            },
        });
    }
    
    public userBalanceForm: FormGroup = new FormGroup({
        amount: new FormControl<number>(0, { nonNullable: true, validators: [Validators.required] }),
        expAmount: new FormControl<number>(0, { nonNullable: true, validators: [Validators.required] }),
        reason: new FormControl<string>("", { nonNullable: true, validators: [Validators.required] }),
    });

    public banUserForm: FormGroup = new FormGroup({
        reason: new FormControl<string>("", { nonNullable: true, validators: [Validators.required] }),
    });

    createFilter() {
        return (data: UserConversionBackofficeViewModel, filter: string): boolean => {
          if (!data) return false;
          
          const searchTerms = JSON.parse(filter);
          return (data.goal?.toLowerCase().includes(searchTerms.goal ?? "") ?? false) &&
                 (data.rejectReason?.toLowerCase().includes(searchTerms.rejectReason ?? "") ?? false) &&
                 (data.forceApproveReason?.toLowerCase().includes(searchTerms.forceApproveReason ?? "") ?? false);
        };
      }

    public applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.conversionsDataSource.filter = JSON.stringify({goal:this._goalFilter()?.value?.trim().toLowerCase(), rejectReason:this._rejectReasonFilter()?.value?.trim().toLowerCase(), forceApproveReason:this._forceApproveReasonFilter()?.value?.trim().toLowerCase()});
        if (this.conversionsDataSource.paginator) {
            this.conversionsDataSource.paginator.firstPage();
        }
        }
}
